import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { logoutUser } from "../../api/services/authService";
import { getLoginPageByRole } from "../../utils/authHelpers";
import { Search, Bell, Menu, User, Settings, LogOut } from "lucide-react";
import LanguageToggle from "../LanguageToggle";
import CyberBrand from "../common/CyberBrand";

const ADMIN_ROLES = ["platform_admin", "university_admin", "company_admin"];

export default function SidebarLayoutHeader({
  onMenuClick,
  isDrawerOpen = false,
}) {
  const { user, logout } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown on outside click — mirrors Navbar behaviour
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const name =
    user?.given_name && user?.family_name
      ? `${user.given_name} ${user.family_name}`
      : user?.preferred_username || user?.username || "Admin User";

  // Compute display role label
  const getDisplayRole = () => {
    if (!user?.roles || user.roles.length === 0) return "User";
    const roleLabelMap = {
      platform_admin: "Platform Admin",
      company_admin: "Company Admin",
      company_user: "Company User",
      student: "Student",
      university_admin: "University Admin",
      university_student: "University Student",
      "default-roles-qv-platform": "Professional",
    };
    const priorityRoles = [
      "platform_admin",
      "company_admin",
      "university_admin",
      "company_user",
      "student",
      "university_student",
      "default-roles-qv-platform",
    ];
    const normalizedRoles = user.roles.map((r) => r.toLowerCase());
    const displayRole =
      priorityRoles.find((r) => normalizedRoles.includes(r)) ||
      normalizedRoles[0];
    return (
      roleLabelMap[displayRole] ||
      displayRole.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  const role = getDisplayRole();
  const email = user?.email || "admin@quantumvanguard.com";
  const initial = name[0]?.toUpperCase() || "P";

  const isAdmin = user?.roles?.some((r) =>
    ADMIN_ROLES.includes(r.toLowerCase()),
  );

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    setIsLoggingOut(true);
    try {
      // Add minimum delay to show loading state
      await Promise.all([
        logoutUser(),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);
      const loginPage = getLoginPageByRole(user?.roles);
      logout();
      navigate(loginPage, { replace: true });
    } catch {
      const loginPage = getLoginPageByRole(user?.roles);
      logout();
      navigate(loginPage, { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleProfile = () => {
    setIsDropdownOpen(false);
    navigate(isAdmin ? "/admin/profile" : "/profile");
  };

  const handleSettings = () => {
    setIsDropdownOpen(false);
    navigate("/admin/settings");
  };

  return (
    <>
      <header
        dir="ltr"
        className={`fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-3 sm:px-6 min-h-16 sm:min-h-19 qv-glass-bar ${
          isDrawerOpen
            ? "max-lg:bg-(--color-bg)! max-lg:backdrop-blur-none!"
            : ""
        }`}
      >
        {/* Brand + hamburger — left side of the global top bar.
            Hamburger is always visible: on mobile it toggles the drawer,
            on desktop it collapses the sidebar to an icon rail. */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={onMenuClick}
            className="p-2 -ml-2 rounded-lg text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg-card) transition-colors"
            aria-label="Toggle navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <CyberBrand size="sm" showSubtitle={false} />
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Search - hidden on mobile, full on desktop */}
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-(--color-border) bg-(--color-bg-card) text-sm">
            <Search size={14} className="text-(--color-text-muted) shrink-0" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search resources..."
              className="bg-transparent outline-none w-32 md:w-44 text-sm text-(--color-text-primary) placeholder-(--color-text-muted)"
            />
          </div>

          {/* Language toggle */}
          <div className="hidden sm:flex">
            <LanguageToggle
              lang={i18n.language}
              toggleLanguage={toggleLanguage}
              standalone={false}
            />
          </div>
          {/* Notification bell */}

          <button className="hidden sm:flex relative p-1.5 sm:p-2 text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors">
            <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full" />
          </button>

          {/* User menu — mirrors Navbar dropdown */}
          <div
            className="relative flex items-center gap-2 sm:gap-3"
            ref={dropdownRef}
          >
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-(--color-text-primary)">
                {name}
              </p>
              <p className="text-xs text-(--color-text-muted)">{role}</p>
            </div>

            <button
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              aria-expanded={isDropdownOpen}
              aria-label="User menu"
              className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-(--color-primary) font-medium text-xs sm:text-sm text-(--color-text-on-primary) transition-all duration-200 hover:opacity-90"
            >
              {initial}
            </button>

            {/* Dropdown panel */}
            {isDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-52 rounded-sm bg-(--color-bg) border border-(--color-border) p-1.5 text-sm shadow-2xl shadow-(--color-shadow-primary)/50 z-50">
                {/* User info header */}
                <div className="px-3 py-2.5 mb-1.5">
                  <p className="font-medium text-(--color-text-primary)">
                    {name}
                  </p>
                  <p className="text-xs text-(--color-text-muted)">{email}</p>
                </div>

                <button
                  onClick={handleProfile}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-(--color-text-primary) transition-colors hover:bg-(--color-primary)/40 hover:text-(--color-text-primary)"
                >
                  <User className="h-4 w-4" />
                  Profile
                </button>

                <button
                  onClick={handleSettings}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-(--color-text-primary) transition-colors hover:bg-(--color-primary)/40 hover:text-(--color-text-primary)"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </button>

                <div className="my-1.5 h-px bg-(--color-border)" />

                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Logout full-screen overlay — matches Navbar/Sidebar pattern */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-9999">
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-(--color-primary)/20 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-(--color-primary) rounded-full animate-spin" />
            </div>
            <p className="text-white font-semibold">Logging out…</p>
          </div>
        </div>
      )}
    </>
  );
}
